import {
  DebouncedSearchInput,
  ListHeader,
  ListHeaderActiveFilters,
  ListHeaderActiveFiltersActionButton,
  ModalOpenButton,
  useFormatting,
  useScreenWidth,
} from "@msys/ui";
import { Box, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import {
  OrganisationProjectsSorting,
  ProjectStateMachineStatus,
} from "../../../clients/graphqlTypes.js";
import { useUserData } from "../../auth/useUserData.js";
import { SwitchCollectionViewButton } from "../../commons/button/SwitchCollectionViewButton.js";
import { FilterButton } from "../../commons/filters/FilterButton.js";
import { useFiltersAndPagination } from "../../commons/filters/useFiltersAndPagination.js";
import {
  CollectionView,
  useCollectionViewWithMobile,
} from "../../commons/hooks/useCollectionView.js";
import { Page, PageTopbarItem } from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { PAGE_LIST_RESULTS_PER_PAGE } from "../../constants.js";
import { OpportunityListItemDisplayConfig } from "../../features/opportunities/components/OpportunityListItem.js";
import {
  Filters,
  OpportunitiesList,
} from "../../features/opportunities/OpportunitiesList.js";
import { defaultListItemDisplayConfig } from "../../features/opportunities/OpportunitiesListItemVisibilitySettingsModal.js";
import { FilterRelevantToMe } from "../../features/projects/filters/index.js";
import { ProjectsFilterModal } from "../../features/projects/ProjectsFilterModal.js";
import { getProjectFilterChipsFactory } from "../../features/projects/renderProjectsFilterChips.js";
import { PROJECT_FILTER_MAP } from "../../features/projects/useProjectSources.js";
import { useDataGridStateStore } from "../../features/users/useDataGridStateStore.js";
import { useUserPreference } from "../../features/users/useUserPreference.js";

const ALLOWED_VIEWS: CollectionView[] = ["table", "list"];
const DEFAULT_SORTING: OrganisationProjectsSorting[] = [
  {
    column: "createdAt",
    direction: "desc",
  },
];

interface Props {
  submenuItems: PageTopbarItem[];
}

export function OpportunitiesPast({ submenuItems }: Props) {
  const { t } = useTranslate([
    "Projects",
    "Tickets",
    "Opportunities",
    "Categories",
    "Global",
    "ProjectMembers",
    "Projects",
  ]);

  const viewer = useUserData().currentUser!;

  const { getFormattedPrice, getFormattedFloat } = useFormatting();

  const [activeView, setActiveView] =
    useCollectionViewWithMobile<CollectionView>(
      "opportunities-past",
      "table",
      "list"
    );
  const { isMinTablet } = useScreenWidth();

  const {
    offset,
    limit,
    paginationModel,
    setPaginationModel,
    filters,
    setFilters,
    resetFilters,
    sorting,
    setSorting,
    toRemoveParams,
  } = useFiltersAndPagination<OrganisationProjectsSorting, Filters>(
    {},
    DEFAULT_SORTING,
    PAGE_LIST_RESULTS_PER_PAGE
  );

  const baseVariables = React.useMemo(
    () => ({
      offset,
      limit,
      sorting: DEFAULT_SORTING,
    }),
    [limit, offset]
  );

  const allowedStates: ProjectStateMachineStatus[] = React.useMemo(
    () => ["contractor_declined", "contractee_declined"],
    []
  );

  const variables = React.useMemo(
    () => ({
      ...baseVariables,
      ...filters,
      filterBySources: filters.sources
        ? filters.sources.map(s => PROJECT_FILTER_MAP[s]).flat(1)
        : undefined,
      filterIncludeState: filters.filterIncludeState?.length
        ? filters.filterIncludeState
        : allowedStates,
      sorting,
    }),
    [baseVariables, allowedStates, filters, sorting]
  );

  const listViewName = "OpportunitiesPast";
  const stateStore = useDataGridStateStore(listViewName);
  const listItemDisplayConfigStore =
    useUserPreference<OpportunityListItemDisplayConfig>(
      `DisplayConfig-${listViewName}`,
      defaultListItemDisplayConfig
    );

  const getProjectFilterChips = getProjectFilterChipsFactory({
    viewer,
    t,
    allowedStates,
    getFormattedFloat,
    showPhasesChip: activeView !== "kanban",
  });
  const activeFilterChips = getProjectFilterChips({
    filters,
    setFilters,
  });

  return (
    <Page
      subtitle={t("Opportunities", { ns: "Opportunities" })}
      title={t("Past", { ns: "Opportunities" })}
      submenuItems={submenuItems}
    >
      <PageContainer>
        <ListHeader
          SwitchViewButton={
            <SwitchCollectionViewButton
              allowedViews={ALLOWED_VIEWS}
              activeView={activeView}
              setActiveView={setActiveView}
            />
          }
          FilterButton={
            <ModalOpenButton
              Modal={ProjectsFilterModal}
              modalProps={{
                sorting,
                setSorting,
                filters,
                setFilters,
                resetFilters,
                allowedStates,
                showPhasesFilter: activeView !== "kanban",
                showSorting: activeView !== "kanban",
              }}
            >
              <FilterButton />
            </ModalOpenButton>
          }
          FilterFields={
            isMinTablet ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  height: 0,
                  pl: 1.5,
                }}
              >
                <FilterRelevantToMe filters={filters} setFilters={setFilters} />
              </Box>
            ) : undefined
          }
          SearchField={
            <DebouncedSearchInput
              placeholder={t("Search", {
                ns: "Global",
              })}
              defaultValue={filters.filterSearchTerm}
              onChangeSearchTerm={newValue =>
                setFilters(filters => ({
                  ...filters,
                  filterSearchTerm: newValue,
                }))
              }
            />
          }
          ActiveFilterChips={
            <ListHeaderActiveFilters
              activeFilterChips={activeFilterChips}
              collapsedContent={
                <Typography variant="body2">
                  {t("Active filters", { ns: "Global" })}
                  <Typography
                    variant="body2"
                    component="span"
                    sx={{
                      marginLeft: 1,
                      fontWeight: "bold",
                    }}
                  >
                    {t("{count} results", {
                      ns: "Global",
                      count: activeFilterChips.length,
                    })}
                  </Typography>
                </Typography>
              }
              clearAllButton={
                <ListHeaderActiveFiltersActionButton
                  label={t("Clear all", { ns: "Global" })}
                  onClick={resetFilters}
                />
              }
            />
          }
          sx={{ mb: 2 }}
        />
        {!listItemDisplayConfigStore.loading && (
          <OpportunitiesList
            sorting={sorting}
            setSorting={setSorting}
            activeView={activeView}
            paginationModel={paginationModel}
            setPaginationModel={setPaginationModel}
            stateStore={stateStore}
            displayConfig={listItemDisplayConfigStore.value}
            variables={variables}
          />
        )}
      </PageContainer>
    </Page>
  );
}
